<form [formGroup]="companyForm">
  <header class="flex w-full flex-col px-4 pb-4 pt-2">
    <div class="flex flex-row items-center justify-between">
      <div class="flex items-center gap-4">
        <p-button
          icon="pi pi-arrow-left"
          (onClick)="backToList()"
          [text]="true"
          [rounded]="true"
          pTooltip="Back to list"
        />
        <h1 class="text-2xl font-semibold text-greyscale-900">
          {{ editMode ? "Edit Company" : "Create Company" }}
        </h1>
      </div>
      <div class="flex items-center gap-4">
        @if (editMode) {
          <p-button
            label="Cancel"
            severity="secondary"
            routerLink="/admin/company"
          />
          <p-button
            label="Save Changes"
            icon="pi pi-check"
            (onClick)="submitForm()"
          />
        }
      </div>
    </div>
    <p class="text-sm text-greyscale-600">
      {{
        editMode
          ? "Update company information and settings"
          : "Create a new company profile to assign users and manage subscriptions"
      }}
    </p>
  </header>
  <main class="p-4">
    <section class="mb-4 rounded-lg border border-gray-200 bg-white p-4">
      <h2 class="mb-4 text-lg text-primary-500">Company Informations</h2>
      <!-- Company Name -->
      <div class="mb-4">
        <label for="companyName" class="text-sm text-primary-500">
          Company Name
        </label>
        <input
          id="companyName"
          type="text"
          pInputText
          formControlName="name"
          [fluid]="true"
        />
      </div>

      <!-- VAT Number -->
      <div class="mb-4">
        <label for="vatNumber" class="text-sm text-primary-500">
          VAT Number
        </label>
        <input
          id="vatNumber"
          type="text"
          pInputText
          formControlName="vatNumber"
          [fluid]="true"
          placeholder="BE0477472701"
        />
        @if (vatFormatWarning) {
          <div class="mt-1 flex items-center gap-1 text-xs text-warning-600">
            <lucide-angular [img]="alertIcon" class="h-3 w-3" />
            <span>{{
              vatFormatWarning.key | transloco: vatFormatWarning.params
            }}</span>
          </div>
        }
      </div>
      <div class="mb-4">
        <label for="size" class="text-sm text-primary-500">
          {{ "accountMenu.billingInformation.companySize" | transloco }}
        </label>
        <p-select
          id="size"
          formControlName="size"
          [placeholder]="
            'accountMenu.billingInformation.placeholder.companySize' | transloco
          "
          [options]="sizeOptions"
          optionValue="value"
          class="w-full"
        >
          <ng-template #selectedItem let-option>
            {{ option.labelKey | transloco }}
          </ng-template>
          <ng-template #item let-option>
            {{ option.labelKey | transloco }}
          </ng-template>
        </p-select>
      </div>
      <!-- Address: Street + Number -->
      <div class="mb-4 flex flex-col gap-4 sm:flex-row">
        <div class="w-full sm:w-2/3">
          <label for="street" class="text-sm text-primary-500"> Street </label>
          <input
            id="street"
            type="text"
            pInputText
            formControlName="street"
            [fluid]="true"
          />
        </div>
        <div class="w-full sm:w-1/3">
          <label for="streetNumber" class="text-sm text-primary-500">
            Street Number
          </label>
          <input
            id="streetNumber"
            type="text"
            pInputText
            formControlName="streetNumber"
            [fluid]="true"
          />
        </div>
      </div>

      <!-- Address: Zip Code + City -->
      <div class="mb-4 flex flex-col gap-4 sm:flex-row">
        <div class="w-full sm:w-1/3">
          <label for="zipCode" class="text-sm text-primary-500">
            Zip Code
          </label>
          <input
            id="zipCode"
            type="text"
            pInputText
            formControlName="zipCode"
            [fluid]="true"
          />
        </div>
        <div class="w-full sm:w-2/3">
          <label for="city" class="text-sm text-primary-500"> City </label>
          <input
            id="city"
            type="text"
            pInputText
            formControlName="city"
            [fluid]="true"
          />
        </div>
      </div>

      <!-- Country -->
      <div class="mb-4">
        <label for="country" class="text-sm text-primary-500"> Country </label>
        <input
          id="country"
          type="text"
          pInputText
          formControlName="country"
          [fluid]="true"
          p-select
        />
      </div>
      <!-- Company Logo (edit mode only) -->
      @if (editMode) {
        <div class="mb-4">
          <div class="mb-1 text-sm text-primary-500">
            {{ "logoUpload.companyLogoLabel" | transloco }}
          </div>
          <app-logo-upload
            [currentLogoUrl]="company?.logoUrl"
            [uploading]="logoUploading"
            shape="square"
            (logoCropped)="onLogoCropped($event)"
          />
        </div>
      }
    </section>
    <!-- Project Restriction Settings -->
    @if (editMode) {
      <section class="mb-4 rounded-lg border border-gray-200 bg-white p-4">
        <h2 class="mb-2 text-lg text-primary-500">Project Restrictions</h2>
        <p class="mb-4 text-sm text-gray-500">
          When enabled, this company will only see projects specifically
          assigned to them. Configure project access from the project edit page.
        </p>
        <div class="flex items-center gap-3">
          @if (updatingRestrictedProjects) {
            <i class="pi pi-spin pi-spinner text-primary-500"></i>
          } @else {
            <p-checkbox
              [ngModel]="company?.hasRestrictedProjects"
              [ngModelOptions]="{ standalone: true }"
              (ngModelChange)="toggleRestrictedProjects($event)"
              [binary]="true"
              inputId="restrictedProjects"
            />
          }
          <label for="restrictedProjects" class="cursor-pointer text-sm">
            Enable restricted project access
          </label>
          @if (company?.hasRestrictedProjects) {
            <span class="rounded bg-blue-100 px-2 py-0.5 text-xs text-blue-700">
              Active
            </span>
          }
        </div>
      </section>
    }

    @if (editMode) {
      @if (company) {
        <app-manage-company-pack [(company)]="company" />
      }

      <section class="mb-4 rounded-lg border border-gray-200 bg-white p-4">
        <div class="mb-4 flex items-center justify-between">
          <h2 class="text-lg text-primary-500">Users in Company</h2>
          <p-button label="Add User" icon="pi pi-plus" (onClick)="addUser()" />
        </div>
        @for (user of userList; track user.id) {
          <article class="my-2 flex items-center justify-between">
            <div>
              <span>{{ user.firstName + " " + user.lastName }}</span>
              <div>
                <span class="text-sm text-gray-500">
                  {{ user.emails[0].email }}
                </span>
              </div>
            </div>
            <div class="flex items-center gap-2">
              <p-button
                icon="pi pi-pencil"
                label="Edit"
                severity="primary"
                variant="outlined"
                (onClick)="editUser(user.id!)"
                size="small"
              />
              <p-button
                icon="pi pi-trash"
                label="Remove"
                severity="danger"
                variant="outlined"
                (onClick)="confirmDelete(user.id!)"
                size="small"
              />
            </div>
          </article>
        }
      </section>
      <section class="mb-4 rounded-lg border border-gray-200 bg-white p-4">
        <div class="mb-4 flex items-center justify-between">
          <h2 class="text-lg text-primary-500">Delete Company</h2>
          <p-button
            icon="pi pi-trash"
            label="Delete Company"
            severity="danger"
            (onClick)="confirmDeleteCompany()"
          />
        </div>
      </section>
    }

    @if (!editMode) {
      <section
        class="mb-4 flex justify-between rounded-lg border border-gray-200 bg-white p-4"
      >
        <p-button
          label="Cancel"
          severity="secondary"
          routerLink="/admin/company"
        />
        <p-button
          label="Create Company"
          icon="pi pi-plus"
          (onClick)="submitForm()"
        />
      </section>
    }
  </main>
</form>
